import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Footer from './components/Footer';
import Navbar from './components/Navbar';
import GlobalAlert from './components/GlobalAlert';
import WorkWithUs from './pages/WorkWithUs';
import { useLoading } from './utils/contexts/LoadingContext';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute, PublicRoute } from './contexts/RouteGuard';
import { Toaster } from './components/ui/toaster';
import { trackPageview } from '@/utils/siteAnalytics';

// Lazy load the pages
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Calendar = lazy(() => import('./pages/Calendar'));
const OurServicesPage = lazy(() => import('./pages/OurServicesPage'));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const GalleriPage = lazy(() => import('./pages/GalleriPage'));
const FormPage = lazy(() => import('./pages/FormContactPage'));

// Admin pages
const AdminLogin = lazy(() => import('./pages/admin/LoginPage'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));

const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const LoadingOverlay = lazy(() => import('./components/LoadingOverlay'));

// Create a wrapper component to handle conditional layout
const AppContent: React.FC = () => {
  const { loading } = useLoading();
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminPage) return;
    trackPageview();
  }, [location.pathname, location.search, isAdminPage]);
  
  return (
    <div className="flex flex-col relative h-screen">
      {!isAdminPage && <Navbar />}
      {!isAdminPage && <GlobalAlert />}
      
      {/* Loading Overlay */}
      <LoadingOverlay loading={loading} />
      
      {/* Suspense component provides a fallback while the lazy-loaded components are being fetched */}
      <Suspense fallback={<LoadingOverlay loading={true} />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<OurServicesPage />} />
          <Route path="/work-with-us" element={<WorkWithUs />} />
          <Route path="/contact" element={<ContactUsPage />} />
          <Route path="/form" element={<FormPage />} />
          <Route path="/galleri" element={<GalleriPage />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/events" element={<EventsPage />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={
            <PublicRoute>
              <AdminLogin />
            </PublicRoute>
          } />
          
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          
          {/* 404 Route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      
      {!isAdminPage && <Footer />}
      <Toaster />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
};

export default App;
